import { motion } from 'framer-motion'
import { Home, ArrowLeft, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'

const NotFound = () => {
  const navigate = useNavigate()

  return (
    <div className='min-h-screen w-full flex flex-col items-center justify-center bg-white relative overflow-hidden'>
      {/* Decorative background elements */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 0.1, scale: 1 }}
        transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse' }}
        className='absolute top-1/4 -left-20 w-96 h-96 bg-[#0071E3] rounded-full blur-[120px] pointer-events-none'
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 0.1, scale: 1 }}
        transition={{
          duration: 3,
          repeat: Infinity,
          repeatType: 'reverse',
          delay: 1,
        }}
        className='absolute bottom-1/4 -right-20 w-96 h-96 bg-[#5E5CE6] rounded-full blur-[120px] pointer-events-none'
      />

      <div className='relative z-10 flex flex-col items-center text-center px-6'>
        {/* Animated Icon/Illustration */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className='mb-8 relative'
        >
          <div className='w-24 h-24 rounded-3xl bg-slate-50 flex items-center justify-center border border-slate-100 shadow-sm'>
            <Search className='w-12 h-12 text-[#1D1D1F]' strokeWidth={1.5} />
          </div>
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className='absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full border-4 border-white'
          />
        </motion.div>

        {/* 404 Text */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          className='text-[120px] font-bold tracking-tighter text-[#1D1D1F] leading-none mb-4 selection:bg-[#0071E3] selection:text-white'
        >
          404
        </motion.h1>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
          className='max-w-md'
        >
          <h2 className='text-3xl font-semibold text-[#1D1D1F] mb-4'>
            Lost in the dashboard?
          </h2>
          <p className='text-[#86868B] text-lg leading-relaxed mb-10'>
            The page you're looking for doesn't exist or has been moved to a
            different section.
          </p>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
          className='flex flex-col sm:flex-row gap-4'
        >
          <Button
            onClick={() => navigate(-1)}
            variant='outline'
            className='h-12 px-8 rounded-full border-[#D2D2D7] hover:bg-[#F5F5F7] text-black font-medium transition-all active:scale-95 flex items-center gap-2'
          >
            <ArrowLeft className='w-4 h-4' />
            Go Back
          </Button>
          <Button
            onClick={() => navigate('/')}
            className='h-12 px-8 rounded-full bg-[#1D1D1F] hover:bg-black text-white font-medium shadow-none transition-all active:scale-95 flex items-center gap-2'
          >
            <Home className='w-4 h-4' />
            Return Home
          </Button>
        </motion.div>
      </div>

      {/* Footer Branding */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className='absolute bottom-10 left-1/2 -translate-x-1/2'
      >
        <span className='text-xs font-semibold tracking-widest text-[#86868B] uppercase'>
          Profitability Dashboard
        </span>
      </motion.div>
    </div>
  )
}

export default NotFound
